import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from 'api/Api';
import { invalidateAuthTags } from 'api/AuthApi';
import { User } from 'api/UsersApi';

export enum AdminAccessStatusEnum {
  Invited = 'invited',
  Accepted = 'accepted',
  Declined = 'declined'
}

export enum AdminAccessRoleEnum {
  Activator = 'activator',
  Manager = 'manager'
}

export enum AdminAccessListEnum {
  Admins = 'admins',
  Users = 'users'
}

export interface AdminAccess {
  id: string;
  admin: User | null;
  administrator?: User | null;
  beneficiary?: User | null;
  user: User | null;
  displayName?: string | null;
  email?: string | null;
  phone?: string | null;
  beneficiaryDisplayName?: string | null;
  beneficiaryEmail?: string | null;
  administratorDisplayName?: string | null;
  administratorEmail?: string | null;
  patientProfile?: {
    id?: string | null;
    userId?: string | null;
    lifecycleStatus?: string | null;
  } | null;
  patientProfileId?: string | null;
  patientLifecycleStatus?: string | null;
  status: AdminAccessStatusEnum;
  role: AdminAccessRoleEnum;
  isActive: string;
  relationship: string;
  guessedAdminName: string;
}

const asStringOrNull = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

const getFirstNonEmptyString = (...values: unknown[]): string | null => {
  for (const value of values) {
    const normalized = asStringOrNull(value);
    if (normalized) {
      return normalized;
    }
  }

  return null;
};

const getFullName = (firstName: unknown, lastName: unknown): string | null => {
  const normalizedFirstName = asStringOrNull(firstName);
  const normalizedLastName = asStringOrNull(lastName);
  const fullName = [normalizedFirstName, normalizedLastName].filter(Boolean).join(' ').trim();
  return fullName || null;
};

export const normalizeAdminAccess = (payload: AdminAccess): AdminAccess => {
  const source = payload as AdminAccess & Record<string, unknown>;
  const userLike = (source.user as (User & Record<string, unknown>) | null | undefined) || null;
  const adminLike = (source.admin as (User & Record<string, unknown>) | null | undefined) || null;
  const administratorLike = (source.administrator as (User & Record<string, unknown>) | null | undefined) || null;
  const beneficiaryLike = (source.beneficiary as (User & Record<string, unknown>) | null | undefined) || null;
  const rawPatientProfile = source.patientProfile as
    | (NonNullable<AdminAccess['patientProfile']> & Record<string, unknown>)
    | undefined;

  const beneficiaryName = getFirstNonEmptyString(
    source.beneficiaryDisplayName,
    getFullName(beneficiaryLike?.firstName, beneficiaryLike?.lastName),
    getFullName(userLike?.firstName, userLike?.lastName),
    getFullName(rawPatientProfile?.firstName, rawPatientProfile?.lastName),
    getFullName(source.beneficiaryFirstName, source.beneficiaryLastName),
    getFullName(source.firstName, source.lastName),
    source.displayName
  );
  const administratorName = getFirstNonEmptyString(
    source.administratorDisplayName,
    source.guessedAdminName,
    getFullName(administratorLike?.firstName, administratorLike?.lastName),
    getFullName(adminLike?.firstName, adminLike?.lastName),
    getFullName(source.adminFirstName, source.adminLastName),
    getFullName(source.administratorFirstName, source.administratorLastName)
  );

  const beneficiaryEmail = getFirstNonEmptyString(
    source.beneficiaryEmail,
    beneficiaryLike?.email,
    userLike?.email,
    rawPatientProfile?.email,
    source.beneficiaryContactEmail,
    source.profileEmail,
    source.email
  );
  const administratorEmail = getFirstNonEmptyString(
    source.administratorEmail,
    adminLike?.email,
    administratorLike?.email,
    source.inviteeEmail,
    source.invitedEmail,
    source.adminEmail,
    source.email
  );

  const beneficiaryPhone = getFirstNonEmptyString(
    source.phone,
    beneficiaryLike?.phone,
    userLike?.phone,
    rawPatientProfile?.phone,
    source.beneficiaryPhone,
    source.profilePhone
  );

  return {
    ...payload,
    user: userLike,
    admin: adminLike,
    administrator: administratorLike,
    beneficiary: beneficiaryLike,
    beneficiaryDisplayName: beneficiaryName,
    beneficiaryEmail,
    administratorDisplayName: administratorName,
    administratorEmail,
    displayName: beneficiaryName,
    email: beneficiaryEmail,
    phone: beneficiaryPhone,
    patientProfile: rawPatientProfile
      ? {
          ...rawPatientProfile,
          id: asStringOrNull(rawPatientProfile.id),
          userId: asStringOrNull(rawPatientProfile.userId),
          lifecycleStatus: asStringOrNull(rawPatientProfile.lifecycleStatus)
        }
      : null,
    patientProfileId:
      asStringOrNull(source.patientProfileId) ??
      asStringOrNull(rawPatientProfile?.id) ??
      asStringOrNull(source.profileId),
    patientLifecycleStatus:
      asStringOrNull(source.patientLifecycleStatus) ?? asStringOrNull(rawPatientProfile?.lifecycleStatus)
  };
};

export const adminAccessApi = createApi({
  baseQuery: baseQueryWithReAuth,
  reducerPath: 'adminAccessApi',
  tagTypes: ['AdminAccess'],
  endpoints: (build) => ({
    getAllAdminAccess: build.query<
      { list: AdminAccess[]; total: number },
      {
        list: AdminAccessListEnum;
        limit?: number;
        offset?: number;
        filter?: { userId?: string; status?: AdminAccessStatusEnum };
      }
    >({
      query: (params) => {
        const filter = {};

        if (params.filter) {
          if (params.filter.userId) {
            filter['userId'] = params.filter.userId;
          }

          if (params.filter.status) {
            filter['status'] = params.filter.status;
          }
        }

        return {
          url: '/admin-access',
          params: { ...params, filter: JSON.stringify(filter) }
        };
      },
      transformResponse: (response: { data: [AdminAccess[], number] }) => ({
        list: response.data[0].map((item) => normalizeAdminAccess(item)),
        total: response.data[1]
      }),
      providesTags: (result, _error, _arg) =>
        result
          ? [
              ...result.list.map(({ id }) => ({
                type: 'AdminAccess' as const,
                id
              })),
              { type: 'AdminAccess', id: 'LIST' }
            ]
          : [{ type: 'AdminAccess', id: 'LIST' }]
    }),
    getOneAdminAccess: build.query<AdminAccess, Pick<AdminAccess, 'id'>>({
      query: ({ id }) => ({ url: `/admin-access/${id}` }),
      transformResponse: (response: { data: AdminAccess }) => normalizeAdminAccess(response.data),
      providesTags: (result, _error, { id }) => [{ type: 'AdminAccess', id }]
    }),
    createAdminAccess: build.mutation<
      AdminAccess,
      Pick<User, 'email'> & Pick<AdminAccess, 'role' | 'relationship' | 'guessedAdminName'>
    >({
      query: (body) => ({
        url: '/admin-access',
        method: 'POST',
        body
      }),
      transformResponse: (response: { data: AdminAccess }) => response.data,
      invalidatesTags: () => [{ type: 'AdminAccess', id: 'LIST' }]
    }),
    editAdminAccess: build.mutation<
      AdminAccess,
      Pick<AdminAccess, 'id' | 'role' | 'relationship' | 'guessedAdminName'>
    >({
      query: ({ id, ...body }) => ({
        url: `/admin-access/${id}`,
        method: 'POST',
        body
      }),
      transformResponse: (response: { data: { adminAccess: AdminAccess } }) => response.data.adminAccess,
      invalidatesTags: (result, _error, { id }) => [{ type: 'AdminAccess', id }]
    }),
    reInviteAdminAccess: build.mutation<boolean, Pick<AdminAccess, 'id'>>({
      query: ({ id }) => ({
        url: `/admin-access/${id}/reinvite`,
        method: 'POST'
      }),
      transformResponse: (response: { success: boolean }) => response.success
    }),
    acceptAdminAccess: build.mutation<AdminAccess, Pick<AdminAccess, 'id'>>({
      query: ({ id }) => ({
        url: `/admin-access/${id}/accept`,
        method: 'POST'
      }),
      transformResponse: (response: { data: AdminAccess }) => response.data,
      invalidatesTags: () => [{ type: 'AdminAccess', id: 'LIST' }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(invalidateAuthTags());
        } catch (e) {}
      }
    }),
    declineAdminAccess: build.mutation<AdminAccess, Pick<AdminAccess, 'id'>>({
      query: ({ id }) => ({
        url: `/admin-access/${id}/decline`,
        method: 'POST'
      }),
      transformResponse: (response: { data: AdminAccess }) => response.data,
      invalidatesTags: () => [{ type: 'AdminAccess', id: 'LIST' }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(invalidateAuthTags());
        } catch (e) {}
      }
    }),
    markPendingDeceasedAdminAccess: build.mutation<boolean, Pick<AdminAccess, 'id'>>({
      query: ({ id }) => ({
        url: `/admin-access/${id}/mark-deceased`,
        method: 'POST'
      }),
      transformResponse: (response: { success: boolean }) => response.success,
      invalidatesTags: (result, _error, { id }) => [
        { type: 'AdminAccess', id },
        { type: 'AdminAccess', id: 'LIST' }
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(invalidateAuthTags());
        } catch (e) {}
      }
    }),
    removeAdminAccess: build.mutation<boolean, Pick<AdminAccess, 'id'>>({
      query: ({ id }) => ({
        url: `/admin-access/${id}`,
        method: 'DELETE'
      }),
      transformResponse: (response: { data: { deleted: boolean } }) => response.data.deleted,
      invalidatesTags: () => [{ type: 'AdminAccess', id: 'LIST' }]
    })
  })
});

export const invalidateAdminTags = () => adminAccessApi.util.invalidateTags([{ type: 'AdminAccess', id: 'LIST' }]);

export const {
  useGetAllAdminAccessQuery,
  useGetOneAdminAccessQuery,
  useCreateAdminAccessMutation,
  useEditAdminAccessMutation,
  useReInviteAdminAccessMutation,
  useAcceptAdminAccessMutation,
  useDeclineAdminAccessMutation,
  useMarkPendingDeceasedAdminAccessMutation,
  useRemoveAdminAccessMutation
} = adminAccessApi;
